import type { GeoJsonLineFeature } from '@/contracts/route'

/**
 * Number of sample groups for progressive loading
 * Features are assigned to groups 0-9, with lower groups shown at lower zoom levels
 */
const SAMPLE_GROUP_COUNT = 10

/**
 * Zoom level configuration for segment sampling (used only for non-expression based filtering)
 * Lower zoom = zoomed out (show less detail)
 * Higher zoom = zoomed in (show more detail)
 */
const ZOOM_SAMPLING_CONFIG = {
  // Zoom levels and their corresponding sampling rates
  levels: [
    { maxZoom: 8, sampleRate: 0.6 }, // Very zoomed out: show 10% of segments
    { maxZoom: 10, sampleRate: 0.65 }, // Zoomed out: show 25% of segments
    { maxZoom: 12, sampleRate: 0.7 }, // Medium zoom: show 50% of segments
    { maxZoom: 14, sampleRate: 0.85 }, // Zoomed in: show 75% of segments
    { maxZoom: 16, sampleRate: 1.0 } // Very zoomed in: show all segments
  ]
}

/**
 * Level of Detail configuration for geometry simplification
 * Tolerance is in map units (degrees for lat/lon)
 */
const LOD_CONFIG = {
  levels: [
    { maxZoom: 9, tolerance: 0.01 }, // Aggressive simplification
    { maxZoom: 11, tolerance: 0.005 }, // Moderate simplification
    { maxZoom: 13, tolerance: 0.001 }, // Light simplification
    { maxZoom: Infinity, tolerance: 0 } // No simplification
  ]
}

/**
 * Determines sampling rate based on current zoom level
 */
export function getSamplingRateForZoom(zoom: number): number {
  for (const level of ZOOM_SAMPLING_CONFIG.levels) {
    if (zoom <= level.maxZoom) {
      return level.sampleRate
    }
  }
  return 1.0
}

/**
 * Determines geometry simplification tolerance based on zoom level
 */
export function getSimplificationToleranceForZoom(zoom: number): number {
  for (const level of LOD_CONFIG.levels) {
    if (zoom <= level.maxZoom) {
      return level.tolerance
    }
  }
  return 0
}

/**
 * Samples features based on sampling rate using deterministic selection
 * Uses feature index to ensure consistent sampling across re-renders
 * Ensures a minimum number of features are always shown to prevent sparse networks
 */
export function sampleFeatures(
  features: GeoJsonLineFeature[],
  sampleRate: number
): GeoJsonLineFeature[] {
  if (sampleRate >= 1.0) {
    return features
  }

  if (sampleRate <= 0) {
    return []
  }

  // Minimum number of features to show for small networks
  const MIN_FEATURES = 10000
  const targetCount = Math.max(MIN_FEATURES, Math.floor(features.length * sampleRate))

  // If we'd show all features anyway, return early
  if (targetCount >= features.length) {
    return features
  }

  // Use deterministic sampling - every Nth feature
  const step = Math.max(1, Math.floor(features.length / targetCount))
  const sampled: GeoJsonLineFeature[] = []

  for (let i = 0; i < features.length; i += step) {
    sampled.push(features[i])
  }

  return sampled
}

/**
 * Simplifies line geometry using Douglas-Peucker algorithm
 * Reduces number of points while preserving overall shape
 */
export function simplifyLineString(
  coordinates: Array<[number, number]>,
  tolerance: number
): Array<[number, number]> {
  if (tolerance <= 0 || coordinates.length <= 2) {
    return coordinates
  }

  return douglasPeucker(coordinates, tolerance)
}

/**
 * Douglas-Peucker algorithm implementation
 * Recursively simplifies a line by removing points that don't contribute significantly
 */
function douglasPeucker(
  points: Array<[number, number]>,
  tolerance: number
): Array<[number, number]> {
  if (points.length <= 2) {
    return points
  }

  // Find the point with maximum distance from line between first and last point
  let maxDistance = 0
  let maxIndex = 0
  const start = points[0]
  const end = points[points.length - 1]

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], start, end)
    if (distance > maxDistance) {
      maxDistance = distance
      maxIndex = i
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = douglasPeucker(points.slice(0, maxIndex + 1), tolerance)
    const right = douglasPeucker(points.slice(maxIndex), tolerance)

    // Combine results (remove duplicate middle point)
    return [...left.slice(0, -1), ...right]
  }

  // If max distance is less than tolerance, return simplified line
  return [start, end]
}

/**
 * Calculates perpendicular distance from a point to a line
 */
function perpendicularDistance(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  const [x, y] = point
  const [x1, y1] = lineStart
  const [x2, y2] = lineEnd

  const dx = x2 - x1
  const dy = y2 - y1

  // Handle case where line is a point
  if (dx === 0 && dy === 0) {
    return Math.sqrt((x - x1) ** 2 + (y - y1) ** 2)
  }

  // Calculate perpendicular distance using cross product
  const numerator = Math.abs(dy * x - dx * y + x2 * y1 - y2 * x1)
  const denominator = Math.sqrt(dx ** 2 + dy ** 2)

  return numerator / denominator
}

/**
 * Checks if a bounding box intersects with viewport bounds
 */
export function intersectsViewport(
  featureBounds: { minLon: number; minLat: number; maxLon: number; maxLat: number },
  viewportBounds: { minLon: number; minLat: number; maxLon: number; maxLat: number }
): boolean {
  return !(
    featureBounds.maxLon < viewportBounds.minLon ||
    featureBounds.minLon > viewportBounds.maxLon ||
    featureBounds.maxLat < viewportBounds.minLat ||
    featureBounds.minLat > viewportBounds.maxLat
  )
}

/**
 * Calculates bounding box for a line feature
 */
export function calculateFeatureBounds(coordinates: Array<[number, number]>): {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
} {
  if (coordinates.length === 0) {
    return { minLon: 0, minLat: 0, maxLon: 0, maxLat: 0 }
  }

  let minLon = coordinates[0][0]
  let maxLon = coordinates[0][0]
  let minLat = coordinates[0][1]
  let maxLat = coordinates[0][1]

  for (let i = 1; i < coordinates.length; i++) {
    const [lon, lat] = coordinates[i]
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }

  return { minLon, minLat, maxLon, maxLat }
}

/**
 * Filters features to only include those visible in viewport
 */
export function filterFeaturesInViewport(
  features: GeoJsonLineFeature[],
  viewportBounds: { minLon: number; minLat: number; maxLon: number; maxLat: number } | null
): GeoJsonLineFeature[] {
  if (!viewportBounds) {
    return features
  }

  return features.filter((feature) => {
    const bounds = calculateFeatureBounds(feature.geometry.coordinates)
    return intersectsViewport(bounds, viewportBounds)
  })
}

/**
 * Applies all optimizations: viewport filtering, sampling, and LOD
 */
export function optimizeFeatures(
  features: GeoJsonLineFeature[],
  zoom: number,
  viewportBounds: { minLon: number; minLat: number; maxLon: number; maxLat: number } | null
): GeoJsonLineFeature[] {
  // 1. Filter by viewport (spatial indexing)
  const viewportFeatures = filterFeaturesInViewport(features, viewportBounds)

  // 2. Apply zoom-based sampling
  const sampleRate = getSamplingRateForZoom(zoom)
  const sampledFeatures = sampleFeatures(viewportFeatures, sampleRate)

  // 3. Apply level of detail (geometry simplification)
  const tolerance = getSimplificationToleranceForZoom(zoom)

  if (tolerance > 0) {
    return sampledFeatures.map((feature) => ({
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: simplifyLineString(feature.geometry.coordinates, tolerance)
      }
    }))
  }

  return sampledFeatures
}

/**
 * Assigns sample groups to features for zoom-based filtering via layer expressions.
 * This allows the layer to show/hide features based on zoom without re-adding data.
 *
 * Features are assigned to groups 0-9 deterministically using a hash of their index.
 * Lower groups (0-2) are shown at all zoom levels, higher groups only at higher zoom.
 */
export function assignSampleGroups(features: GeoJsonLineFeature[]): GeoJsonLineFeature[] {
  return features.map((feature, index) => ({
    ...feature,
    properties: {
      ...feature.properties,
      _sampleGroup: index % SAMPLE_GROUP_COUNT
    }
  }))
}

/**
 * Prepares features for rendering with sample groups and optional geometry simplification.
 * Used for one-time loading of features with zoom-based layer filtering.
 */
export function prepareNetworkFeatures(features: GeoJsonLineFeature[]): GeoJsonLineFeature[] {
  // Assign sample groups for zoom-based filtering
  return assignSampleGroups(features)
}
